import { fetchAuthSession } from 'aws-amplify/auth'; // Import fetchAuthSession
import { Download, Search } from 'lucide-react'; // Using lucide-react for icons
import React, { useEffect, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import { clampMonthToCurrent, getCurrentMonthInputValue, getPreviousMonthInputValue } from '../../utils/dateHelpers';

const EmployeeReport = () => {
    // Log screen view
    useScreenLogging('EmployeeReport', {
        component: 'EmployeeReport',
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(() => getPreviousMonthInputValue()); // Stores YYYY-MM string
    const [selectedYear, setSelectedYear] = useState('');
    const [allReports, setAllReports] = useState([]);
    const [reports, setReports] = useState([]);
    const [loadingReports, setLoadingReports] = useState(false);
    const [error, setError] = useState(null); // State for API errors

    // Base URL for API calls
    const BASE_URL = 'https://api.innoworx.ai/Stage'; // From Reports API Documentation

    // Cache configuration
    const CACHE_CONFIG = {
        REPORTS_TTL: 30 * 60 * 1000, // 30 minutes for employee reports
        CACHE_PREFIX: 'employeeReports_cache_',
    };

    // Cache utility functions
    const getCacheKey = (type, ...params) => {
        return `${CACHE_CONFIG.CACHE_PREFIX}${type}_${params.join('_')}`;
    };

    const getCachedData = (cacheKey) => {
        try {
            const cached = localStorage.getItem(cacheKey);
            if (!cached) return null;

            const { data, timestamp, ttl } = JSON.parse(cached);
            const now = Date.now();
            
            // Check if cache is expired
            if (now - timestamp > ttl) {
                localStorage.removeItem(cacheKey);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Error reading from cache:', error);
            localStorage.removeItem(cacheKey);
            return null;
        }
    };

    const setCachedData = (cacheKey, data, ttl) => {
        try {
            const cacheEntry = {
                data,
                timestamp: Date.now(),
                ttl,
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        } catch (error) {
            console.error('Error writing to cache:', error);
            // If storage is full, try to clear old cache entries
            clearExpiredCache();
        }
    };

    const clearExpiredCache = () => {
        try {
            const keys = Object.keys(localStorage);
            keys.forEach(key => {
                if (key.startsWith(CACHE_CONFIG.CACHE_PREFIX)) {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        try {
                            const { timestamp, ttl } = JSON.parse(cached);
                            if (Date.now() - timestamp > ttl) {
                                localStorage.removeItem(key);
                            }
                        } catch (e) {
                            // Invalid cache entry, remove it
                            localStorage.removeItem(key);
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Error clearing expired cache:', error);
        }
    };

    // Cleanup expired cache entries on mount
    useEffect(() => {
        clearExpiredCache();
    }, []);

    // Function to get the authentication token
    const getAuthToken = async () => {
        try {
            const { tokens } = await fetchAuthSession();
            if (tokens && tokens.idToken) {
                return tokens.idToken.toString();
            } else {
                console.error('No ID Token found in the current session.');
                return null;
            }
        } catch (error) {
            console.error('Error fetching authentication session:', error);
            return null;
        }
    };

    // Function to format month input (YYYY-MM) for API (MM-YYYY)
    const formatMonthForApi = (monthString) => {
        if (!monthString) return '';
        const [year, month] = monthString.split('-');
        if (!year || !month) return '';
        return `${String(month).padStart(2, '0')}-${year}`;
    };

    const fetchReportsForMonth = async (monthYear, headers, useCache = true) => {
        // Check cache first if caching is enabled
        if (useCache) {
            const cacheKey = getCacheKey('month', monthYear);
            const cachedData = getCachedData(cacheKey);
            if (cachedData !== null) {
                // null means no cache, but empty array is valid cached data (no reports found)
                return cachedData;
            }
        }

        const apiUrl = `${BASE_URL}/report/users?month-year=${monthYear}`;
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers,
        });

        if (response.status === 404) {
            const emptyReports = [];
            // Cache the empty result as well
            if (useCache) {
                const cacheKey = getCacheKey('month', monthYear);
                setCachedData(cacheKey, emptyReports, CACHE_CONFIG.REPORTS_TTL);
            }
            return emptyReports;
        }

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }

        const data = await response.json();

        if (!Array.isArray(data)) {
            throw new Error(`Unexpected data format for ${monthYear}`);
        }

        // Cache the results
        if (useCache) {
            const cacheKey = getCacheKey('month', monthYear);
            setCachedData(cacheKey, data, CACHE_CONFIG.REPORTS_TTL);
        }

        return data;
    };

    // Function to fetch reports from the API
    useEffect(() => {
        const fetchReports = async () => {
            const hasMonth = Boolean(selectedMonth);
            const hasYear = Boolean(selectedYear);

            if (!hasMonth && !hasYear) {
                setAllReports([]);
                setReports([]);
                return;
            }

            setLoadingReports(true);
            setError(null); // Clear previous errors

            // Check cache first for the overall query
            let cacheKey;
            if (hasMonth) {
                const formattedMonthYear = formatMonthForApi(selectedMonth);
                if (!formattedMonthYear) {
                    setAllReports([]);
                    setReports([]);
                    setLoadingReports(false);
                    return;
                }
                cacheKey = getCacheKey('query', 'month', formattedMonthYear);
            } else if (hasYear) {
                cacheKey = getCacheKey('query', 'year', selectedYear);
            }

            const cachedReports = getCachedData(cacheKey);
            if (cachedReports !== null) {
                // null means no cache, but empty array is valid cached data (no reports found)
                setAllReports(cachedReports);
                setLoadingReports(false);
                setError(null);
                return;
            }

            try {
                const token = await getAuthToken();
                if (!token) {
                    setError('Authentication token is missing. Please ensure a user is logged in.');
                    setLoadingReports(false);
                    return;
                }

                const headers = {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                };

                let fetchedReports = [];
                let formattedMonthYear = null; // Define outside the if block for logging

                if (hasMonth) {
                    formattedMonthYear = formatMonthForApi(selectedMonth);
                    if (!formattedMonthYear) {
                        setAllReports([]);
                        setReports([]);
                        setLoadingReports(false);
                        return;
                    }

                    fetchedReports = await fetchReportsForMonth(formattedMonthYear, headers);
                } else if (hasYear) {
                    const months = Array.from({ length: 12 }, (_, index) => {
                        const monthNumber = String(index + 1).padStart(2, '0');
                        return `${monthNumber}-${selectedYear}`;
                    });

                    const yearlyData = await Promise.all(months.map(async (monthYear) => {
                        try {
                            return await fetchReportsForMonth(monthYear, headers);
                        } catch (err) {
                            // If a specific month fails, log it and continue with others
                            console.error(`Error fetching reports for ${monthYear}:`, err);
                            return [];
                        }
                    }));

                    fetchedReports = yearlyData.flat();
                }

                setAllReports(fetchedReports);
                
                // Cache the combined result
                if (cacheKey) {
                    setCachedData(cacheKey, fetchedReports, CACHE_CONFIG.REPORTS_TTL);
                }
                
                // Log API event for viewing employee report
                const logParams = hasMonth 
                    ? { month: formattedMonthYear, type: 'month' }
                    : { year: selectedYear, type: 'year' };
                
                loggingService.logInfo(
                    'report_view_employee',
                    `User viewed employee report for ${logParams.type} ${hasMonth ? formattedMonthYear : selectedYear}`,
                    {
                        endpoint: '/report/users',
                        method: 'GET',
                        ...logParams,
                        reportCount: fetchedReports.length,
                    }
                ).catch(logError => {
                    console.warn('[EmployeeReport] Failed to log event:', logError);
                });

            } catch (err) {
                console.error("Error fetching employee reports:", err);
                
                // Log API error to logging service
                loggingService.logError(
                    'api_error_employee_report',
                    `Employee Report API error: ${err.message}`,
                    err,
                    {
                        endpoint: '/report/users',
                        method: 'GET',
                        selectedMonth: selectedMonth || null,
                        selectedYear: selectedYear || null,
                        errorMessage: err.message,
                        errorStack: err.stack || null,
                    }
                ).catch(logError => {
                    console.warn('[EmployeeReport] Failed to log error:', logError);
                });
                
                setError(`Failed to load employee reports. Please try again. Error: ${err.message}`);
                setAllReports([]);
                setReports([]); // Clear reports on error
            } finally {
                setLoadingReports(false);
            }
        };

        // Only fetch if BASE_URL is not the placeholder
        if (BASE_URL !== 'YOUR_API_BASE_URL') {
            const timer = setTimeout(() => {
                fetchReports();
            }, 300);

            return () => clearTimeout(timer);
        } else {
            setError("API Base URL is not configured. Please update BASE_URL in EmployeeReport.js.");
            setLoadingReports(false);
            setReports([]);
        }
    }, [selectedMonth, selectedYear, BASE_URL]); // Re-run when selection changes

    useEffect(() => {
        const filteredReports = allReports.filter(report => {
            const fileName = report.file_name ? report.file_name.toLowerCase() : '';
            return fileName.includes(searchTerm.toLowerCase());
        });

        setReports(filteredReports);
    }, [searchTerm, allReports]);

    // handleDownload now uses the 'url' from the API response
    const handleDownload = (reportUrl) => {
        if (!reportUrl) {
            setError('No download URL available for this report.');
            return;
        }

        // Open the URL in a new tab to initiate download
        window.open(reportUrl, '_blank', 'noopener,noreferrer');
    };

    return (
        <div className="flex flex-col flex-1 overflow-hidden">
            {/* Header Title */}
            <div className="self-start mb-6">
                <h1 className="text-black text-3xl font-bold font-['Figtree'] leading-10">Employee Report</h1>
            </div>

            {/* Inner content wrapper with padding and gap */}
            <div className="flex-1 self-stretch flex flex-col justify-start items-start ">
                {/* Controls Bar: Date Picker and Search Bar (Swapped Positions - now vertical) */}
                <div className="self-stretch flex flex-col md:flex-row gap-4 mb-6">
                    <div className="relative w-full md:max-w-sm">
                        <label className="block mb-2 text-sm font-semibold text-neutral-700">Filter by Month</label>
                        <input
                            type="month"
                            className={`block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug ${selectedMonth ? 'outline-green-700 text-green-700' : 'outline-white text-neutral-900'}`}
                            value={selectedMonth}
                            max={getCurrentMonthInputValue()}
                            onChange={(e) => {
                                const rawValue = e.target.value;
                                const nextValue = clampMonthToCurrent(rawValue) || getPreviousMonthInputValue();
                                setSelectedMonth(nextValue);
                                if (nextValue) {
                                    setSelectedYear('');
                                }
                                setAllReports([]);
                                setReports([]);
                            }}
                        />
                    </div>
                    
                </div>

                {/* Reports Header */}
                <div className="inline-flex justify-center items-center gap-2.5">
                    <div className="justify-center text-black text-lg font-bold font-['Figtree'] leading-10">Reports</div>
                </div>

                {/* Search Employee Input Field (now below the date picker) */}
                <div data-property-1="Normal" className="w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white backdrop-blur-[30px] flex flex-col justify-center items-start gap-2.5 mb-3">
                        <div className="inline-flex justify-start items-center gap-4 w-full">
                            <Search className="w-6 h-6 text-neutral-400" />
                            <input
                                type="text"
                                placeholder="Search Employee"
                                className="flex-grow bg-transparent outline-none text-neutral-900 text-lg font-bold font-['Figtree'] leading-snug placeholder-neutral-400"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                {/* List of Report Items */}
                <div className="self-stretch flex flex-col justify-start items-start gap-6">
                    {loadingReports ? (
                        <div className="flex items-center justify-center w-full h-40 text-gray-500">Loading Reports...</div>
                    ) : error ? (
                        <div className="flex items-center justify-center w-full h-40 text-red-500">{error}</div>
                    ) : reports.length > 0 ? (
                        reports.map((report, index) => ( // Added index for unique key if report.id is not guaranteed unique
                            <div
                                key={report.url || index}
                                className="self-stretch h-16 px-6 py-2.5 bg-neutral-50/10 rounded-lg shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-white/0 backdrop-blur-[30px] flex flex-col justify-center items-start gap-2.5 cursor-pointer transition-shadow hover:shadow-lg"
                                role="button"
                                tabIndex={0}
                                onClick={() => handleDownload(report.url)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleDownload(report.url);
                                    }
                                }}
                            >
                                <div className="self-stretch inline-flex justify-between items-center">
                                    <div className="flex justify-start items-center gap-6">
                                    <div className="flex justify-center items-center gap-2.5">
                                            <div className={`${typography.p} text-black font-semibold`}>{report.file_name}</div> {/* Display file_name */}
                                        </div>
                                    </div>
                                    <button
                                        className="w-10 h-10 p-2 bg-blue-700 rounded flex justify-center items-center gap-2.5 cursor-pointer hover:bg-blue-800 transition-colors"
                                        onClick={event => {
                                            event.stopPropagation();
                                            handleDownload(report.url);
                                        }} // Pass report.url for download
                                    >
                                        <Download className="w-6 h-6 text-white stroke-white" strokeWidth={2} />
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="flex items-center justify-center w-full h-40 text-gray-500">
                            {selectedMonth || selectedYear
                                ? 'No reports found for the selected criteria.'
                                : 'Please select a month or enter a year.'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployeeReport;
